import React from "react";
import {
  Sparkles, ThumbsUp, AlertTriangle, Target, ScanLine, Tag,
  ArrowUpRight, Info, Clock,
} from "lucide-react";
import { AppBadge, AppCard, AppCardContent, Callout } from "../ui";
import ScoreRing, { ScoreBar } from "./ScoreRing";
import { fmtDate, scoreBand, TONE_CLASSES } from "./constants";

/**
 * ResumeReport — one completed AI analysis, rendered.
 *
 * Shared by the student's own portfolio and the staff read-only view, so both
 * populations read the same words about the same resume. `compact` drops the
 * per-suggestion examples for the staff list, where the point is triage rather
 * than coaching.
 *
 * ⚠️ The disclaimer at the foot is not decoration and must not be removed. This
 * is a language model's read of text extracted from a PDF; it degrades on
 * unusual layouts and it is shown to an instructor who could otherwise take it
 * as an assessment of the student. See models/ResumeAnalysis.js.
 */

const PRIORITY_TONE = { high: "danger", medium: "warning", low: "info" };
const PRIORITY_LABEL = { high: "Do first", medium: "Worth doing", low: "Nice to have" };

/** A titled list of short strings with a leading icon. Used four times below. */
const Chips = ({ icon: Icon, title, items, tone = "muted", hint }) => {
  if (!items?.length) return null;
  const t = TONE_CLASSES[tone];
  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <div className="tw-flex tw-items-center tw-gap-1.5">
        <Icon size={15} className={t.text} />
        <span className="tw-text-sm tw-font-semibold tw-text-content">{title}</span>
      </div>
      {hint && <p className="tw-m-0 tw-text-xs tw-text-content-subtle">{hint}</p>}
      <div className="tw-flex tw-flex-wrap tw-gap-1.5">
        {items.map((k) => (
          <span
            key={k}
            className={`tw-inline-flex tw-items-center tw-rounded-full tw-border tw-border-solid tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium ${t.bg} ${t.border} ${t.text}`}
          >
            {k}
          </span>
        ))}
      </div>
    </div>
  );
};

/** A bulleted list with a coloured marker. */
const PointList = ({ icon: Icon, title, items, tone }) => {
  if (!items?.length) return null;
  const t = TONE_CLASSES[tone];
  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <div className="tw-flex tw-items-center tw-gap-1.5">
        <Icon size={15} className={t.text} />
        <span className="tw-text-sm tw-font-semibold tw-text-content">{title}</span>
      </div>
      <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-1.5 tw-p-0">
        {items.map((s, i) => (
          <li key={i} className="tw-flex tw-gap-2 tw-text-[0.85rem] tw-leading-relaxed tw-text-content-muted">
            <span className={`tw-mt-1.5 tw-h-1.5 tw-w-1.5 tw-shrink-0 tw-rounded-full tw-bg-current ${t.text}`} />
            <span>{s}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ResumeReport = ({ analysis, compact = false }) => {
  if (!analysis) return null;

  if (analysis.status === "failed") {
    return (
      <Callout tone="danger">
        <span>
          <strong>This analysis did not finish.</strong>{" "}
          {analysis.error || "The analysis could not be completed."}
        </span>
      </Callout>
    );
  }

  const {
    overallScore, roleFitScore, atsScore, summary, sections = [],
    strengths = [], weaknesses = [], missingKeywords = [], unevidencedSkills = [],
    suggestions = [], targetRole, model, completedAt, truncated,
  } = analysis;

  return (
    <div className="tw-flex tw-flex-col tw-gap-5">
      {/* ── Headline: three scores that answer three different questions ── */}
      <div className="tw-flex tw-flex-col tw-gap-5 tw-rounded-md tw-border tw-border-solid tw-border-line-soft tw-bg-surface-sunken tw-p-5 min-[880px]:tw-flex-row min-[880px]:tw-items-center">
        <ScoreRing value={overallScore} label="Overall" sublabel={targetRole ? `for ${targetRole}` : "general"} />

        <div className="tw-min-w-0 tw-flex-1">
          {summary && (
            <p className="tw-m-0 tw-mb-4 tw-text-[0.9rem] tw-leading-relaxed tw-text-content">{summary}</p>
          )}
          <div className="tw-grid tw-gap-3 min-[520px]:tw-grid-cols-2">
            <SubScore
              icon={Target}
              label="Role fit"
              value={roleFitScore}
              hint={targetRole ? `Match against ${targetRole}` : "Name a target role for a sharper read"}
            />
            <SubScore
              icon={ScanLine}
              label="ATS readability"
              value={atsScore}
              hint="Whether screening software can parse it"
            />
          </div>
        </div>
      </div>

      {truncated && (
        <Callout tone="warning">
          This document was longer than the analysis window, so the end of it was not read.
          A resume is best kept to one or two pages.
        </Callout>
      )}

      {/* ── Section breakdown ── */}
      {sections.length > 0 && (
        <AppCard>
          <AppCardContent className="tw-flex tw-flex-col tw-gap-4">
            <h4 className="tw-m-0 tw-text-sm tw-font-semibold tw-text-content">Where the score comes from</h4>
            <div className="tw-grid tw-gap-4 min-[720px]:tw-grid-cols-2">
              {sections.map((s) => (
                <ScoreBar key={s.key} value={s.score} label={s.label || s.key} weight={s.weight} verdict={s.verdict} />
              ))}
            </div>
          </AppCardContent>
        </AppCard>
      )}

      {/* ── Strengths / weaknesses ── */}
      {(strengths.length > 0 || weaknesses.length > 0) && (
        <div className="tw-grid tw-gap-4 min-[720px]:tw-grid-cols-2">
          {strengths.length > 0 && (
            <AppCard><AppCardContent>
              <PointList icon={ThumbsUp} title="What is working" items={strengths} tone="success" />
            </AppCardContent></AppCard>
          )}
          {weaknesses.length > 0 && (
            <AppCard><AppCardContent>
              <PointList icon={AlertTriangle} title="What is holding it back" items={weaknesses} tone="warning" />
            </AppCardContent></AppCard>
          )}
        </div>
      )}

      {/* ── Keyword gaps. Two lists, and the difference matters: one is a skill
             to learn, the other is a skill to mention. ── */}
      {(missingKeywords.length > 0 || unevidencedSkills.length > 0) && (
        <AppCard>
          <AppCardContent className="tw-flex tw-flex-col tw-gap-5">
            <Chips
              icon={Tag}
              title="Missing for this role"
              items={missingKeywords}
              tone="danger"
              hint="Skills and tools this role expects that your resume never mentions."
            />
            <Chips
              icon={Info}
              title="On your profile but not in your resume"
              items={unevidencedSkills}
              tone="info"
              hint="You have listed these under your skills. Add a line of evidence for each — a project, a task, a number."
            />
          </AppCardContent>
        </AppCard>
      )}

      {/* ── The actionable half ── */}
      {suggestions.length > 0 && (
        <AppCard>
          <AppCardContent className="tw-flex tw-flex-col tw-gap-4">
            <div className="tw-flex tw-items-center tw-gap-1.5">
              <Sparkles size={15} className="tw-text-primary" />
              <h4 className="tw-m-0 tw-text-sm tw-font-semibold tw-text-content">What to change, in order</h4>
            </div>
            <ol className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-3 tw-p-0">
              {suggestions.map((s, i) => {
                const tone = TONE_CLASSES[PRIORITY_TONE[s.priority] || "info"];
                return (
                  <li
                    key={i}
                    className="tw-flex tw-flex-col tw-gap-2 tw-rounded-sm tw-border tw-border-solid tw-border-line-soft tw-bg-surface-sunken tw-p-3.5"
                  >
                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                      <span className={`tw-inline-flex tw-items-center tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[0.68rem] tw-font-semibold tw-uppercase tw-tracking-caps ${tone.bg} ${tone.text}`}>
                        {PRIORITY_LABEL[s.priority] || s.priority}
                      </span>
                      {s.area && (
                        <span className="tw-text-xs tw-font-medium tw-text-content-muted">{s.area}</span>
                      )}
                    </div>
                    {s.issue && (
                      <p className="tw-m-0 tw-text-[0.85rem] tw-leading-relaxed tw-text-content-muted">{s.issue}</p>
                    )}
                    {s.action && (
                      <p className="tw-m-0 tw-flex tw-gap-1.5 tw-text-[0.875rem] tw-font-medium tw-leading-relaxed tw-text-content">
                        <ArrowUpRight size={15} className="tw-mt-0.5 tw-shrink-0 tw-text-primary" />
                        <span>{s.action}</span>
                      </p>
                    )}
                    {!compact && s.example && (
                      <div className="tw-rounded-md tw-border tw-border-solid tw-border-primary/20 tw-bg-primary/[0.06] tw-px-3 tw-py-2">
                        <div className="tw-mb-1 tw-text-[0.68rem] tw-font-semibold tw-uppercase tw-tracking-caps tw-text-primary">
                          Try this instead
                        </div>
                        <p className="tw-m-0 tw-text-[0.85rem] tw-italic tw-leading-relaxed tw-text-content">{s.example}</p>
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          </AppCardContent>
        </AppCard>
      )}

      {/* ── Provenance + the disclaimer. See the component header. ── */}
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1 tw-text-xs tw-text-content-subtle">
        <span className="tw-inline-flex tw-items-center tw-gap-1">
          <Clock size={12} /> Analysed {fmtDate(completedAt, { hour: "numeric", minute: "2-digit" })}
        </span>
        {model && <span>· {model}</span>}
      </div>
      <p className="tw-m-0 tw-text-xs tw-leading-relaxed tw-text-content-subtle">
        This score is written guidance from an AI model reading the text of your file. It is a
        coaching tool, not an assessment — no result, eligibility or placement decision here
        depends on it.
      </p>
    </div>
  );
};

/** One of the two secondary scores beside the ring. */
const SubScore = ({ icon: Icon, label, value, hint }) => {
  const has = value !== null && value !== undefined;
  return (
    <div className="tw-rounded-sm tw-border tw-border-solid tw-border-line-soft tw-bg-surface-raised tw-px-3.5 tw-py-3">
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-medium tw-uppercase tw-tracking-caps tw-text-content-subtle">
          <Icon size={13} /> {label}
        </span>
        <span className="tw-text-lg tw-font-semibold tw-tabular-nums tw-text-content">
          {has ? value : "—"}
        </span>
      </div>
      {hint && <p className="tw-m-0 tw-mt-1 tw-text-[0.72rem] tw-leading-snug tw-text-content-subtle">{hint}</p>}
    </div>
  );
};

/** The one-line score summary for a card header or a list row. */
export const ScorePill = ({ score, size = "md" }) => {
  const has = score !== null && score !== undefined;
  if (!has) return <AppBadge variant="secondary" size="sm">Not scored</AppBadge>;
  const tone = TONE_CLASSES[scoreBand(score).tone];
  return (
    <span
      className={`tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-border tw-border-solid tw-font-semibold tw-tabular-nums ${tone.bg} ${tone.border} ${tone.text} ${
        size === "sm" ? "tw-px-2 tw-py-0.5 tw-text-xs" : "tw-px-2.5 tw-py-1 tw-text-sm"
      }`}
    >
      {Math.round(score)}<span className="tw-font-normal tw-opacity-70">/100</span>
    </span>
  );
};

export default ResumeReport;
